import { useEffect } from "react";
import { useSearchParams } from "react-router-dom";

import { Button } from "@/components/ui/button";
import { PageHeader } from "@/components/shared/page-header";
import { useTranslation } from "@/i18n/context";
import { PlaygroundChatColumn } from "./components/chat-column";
import { PlaygroundGovernancePanel } from "./components/governance-panel";
import { PlaygroundSessionsPanel } from "./components/sessions-panel";
import "./tokens.css";
import { usePlayground } from "./use-playground";
import { usePlaygroundCatalog } from "./use-catalog";
import { usePlaygroundSkills } from "./use-skills";

/**
 * Playground page — a dashboard feature page hosting the 3-pane
 * governance-observable chat surface (sessions · chat · governance).
 *
 * Mounts inside the dashboard ``Shell``, so the existing left
 * sidebar provides global navigation. The page negates the Shell's
 * content padding to claim the full content width + viewport
 * height; only the dashboard sidebar sits outside.
 *
 * Keyboard shortcuts:
 *
 * * ``⌘N`` — new session
 * * ``⌘\\`` — toggle governance panel
 */
export default function PlaygroundPage() {
  const pg = usePlayground();
  const catalog = usePlaygroundCatalog();
  const skillsState = usePlaygroundSkills();
  const [searchParams, setSearchParams] = useSearchParams();
  const { t } = useTranslation();

  // Hydration: ensure at least one session exists so the empty CTA flows
  // straight into a usable chat surface.
  useEffect(() => {
    if (pg.hydrated && pg.sessionIndex.length === 0) {
      pg.newSession();
    }
  }, [pg.hydrated, pg.sessionIndex.length, pg]);

  // If the last assistant message returned NO_ACTIVE_CREDENTIAL, refetch
  // the catalog so the chat column flips to the empty state instead of
  // sitting on a stale ``hasActive=true`` from before the credential
  // was revoked.
  const boundErrorCode = pg.boundGovernance?.error?.code;
  useEffect(() => {
    if (boundErrorCode === "NO_ACTIVE_CREDENTIAL") {
      catalog.refetch();
    }
  }, [boundErrorCode, catalog]);

  // Deep-link from /skills "Try in Playground" — when a ``?skill=<id>``
  // param is present and we know that skill, attach it to the active
  // session. Strip the param afterwards so a refresh doesn't keep
  // overriding the operator's later choices.
  const requestedSkillId = searchParams.get("skill");
  useEffect(() => {
    if (!requestedSkillId) return;
    if (!pg.activeSession) return;
    const skill = skillsState.skills.find((s) => s.id === requestedSkillId);
    if (!skill) return;
    const version = skill.current_version ?? 1;
    pg.updateSession(pg.activeSession.id, {
      skillRef: { skillId: skill.id, version },
      systemPrompt: skill.current_version_snapshot?.system_prompt ?? "",
      systemPromptLocked: true,
    });
    const next = new URLSearchParams(searchParams);
    next.delete("skill");
    setSearchParams(next, { replace: true });
  }, [
    requestedSkillId,
    pg.activeSession,
    pg,
    skillsState.skills,
    searchParams,
    setSearchParams,
  ]);

  // Global keyboard shortcuts.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      const isMeta = e.metaKey || e.ctrlKey;
      if (!isMeta) return;
      if (e.key.toLowerCase() === "n") {
        e.preventDefault();
        pg.newSession();
      } else if (e.key === "\\") {
        e.preventDefault();
        pg.toggleGovPanel();
      }
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [pg]);

  return (
    <div
      className="playground-root flex flex-col -mx-4 -my-6 tablet:-mx-6 desktop:-mx-8 desktop:-my-8"
      style={{ height: "calc(100vh - 1px)" }}
    >
      {/* Page header — same title block component as every other
          dashboard page, so the playground stops looking bespoke. */}
      <div
        className="shrink-0 border-b px-6 py-4"
        style={{
          borderColor: "var(--pg-border)",
          background: "var(--pg-surface)",
        }}
      >
        <PageHeader
          title={t("playground.page.title")}
          description={t("playground.page.description")}
        />
      </div>
      <div
        className="playground-shell"
        data-gov-collapsed={pg.govPanelOpen ? "false" : "true"}
      >
        <PlaygroundSessionsPanel
          sessions={pg.sessions}
          sessionIndex={pg.sessionIndex}
          activeSessionId={pg.activeSessionId}
          onCreate={pg.newSession}
          onSelect={pg.setActiveSession}
          onDelete={pg.deleteSession}
        />
      {pg.activeSession ? (
        <PlaygroundChatColumn
          session={pg.activeSession}
          isStreaming={pg.isStreaming}
          boundMessageId={pg.boundMessageId}
          onSend={(content) => void pg.send(content)}
          onStop={pg.abort}
          onUpdateSession={(patch) =>
            pg.activeSession && pg.updateSession(pg.activeSession.id, patch)
          }
          onBindMessage={pg.bindMessage}
          hasActiveCredential={catalog.catalog?.has_active ?? null}
          availableModels={catalog.activeModels}
          catalogModels={catalog.models}
          catalogLoading={catalog.loading}
          skills={skillsState.skills}
        />
      ) : (
        <section
          className="flex h-full items-center justify-center"
          style={{ background: "var(--pg-bg)" }}
        >
          <div
            className="rounded-xl border px-6 py-5 text-center text-xs"
            style={{
              borderColor: "var(--pg-border)",
              background: "var(--pg-surface)",
              color: "var(--pg-text-2)",
            }}
          >
            <p
              className="font-medium text-sm"
              style={{ color: "var(--pg-text)" }}
            >
              {t("playground.page.noSessionSelected")}
            </p>
            <Button
              variant="default"
              size="sm"
              onClick={pg.newSession}
              className="mt-3"
            >
              {t("playground.page.startSession")}
            </Button>
          </div>
        </section>
      )}
        <PlaygroundGovernancePanel
          open={pg.govPanelOpen}
          onToggle={pg.toggleGovPanel}
          message={pg.boundGovernance}
        />
      </div>
    </div>
  );
}
